import React, { useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TextInput, TouchableOpacity, Alert, Image, ActivityIndicator, Modal, Pressable, Platform, BackHandler } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { Colors, SIZES } from '../constants/theme';
import { Button } from '../components/Button';
import ScreenBackground from '../components/ScreenBackground';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { storageService } from '../services/storageService';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';
import { CATEGORIAS_DE_NEGOCIO, nombreDeCategoria } from '../constants/categoriasDeNegocio';
import { validarCedula } from '../utils/cedula';
import { validarTelefono, validarCorreo } from '../utils/telefono';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AVISAR Y PREGUNTAR, QUE EN WEB NO ES LO MISMO QUE EN EL TELÉFONO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Alert.alert` en react-native-web es, literalmente, una función vacía:
 * `class Alert { static alert() {} }`. Esta pantalla hacía 17 llamadas crudas,
 * y las consecuencias eran dos, las dos graves:
 *
 *   1. Los avisos de validación no se pintaban. La persona dejaba un campo mal,
 *      tocaba «Continuar», el `return` cortaba el avance, y en pantalla no
 *      pasaba ABSOLUTAMENTE NADA. Un botón que no responde.
 *   2. Peor: la ÚNICA salida del formulario vivía dentro del `onPress` del
 *      aviso de éxito. En web la solicitud se guardaba bien en Firestore y la
 *      navegación no ocurría jamás — la persona se quedaba encerrada mirando
 *      un formulario ya enviado. Eso es lo que el fundador describió como
 *      «el usuario queda atrapado, no lo deja salir».
 *
 * La regla que queda: en esta pantalla NO se llama a `Alert.alert` a pelo, y
 * NINGUNA navegación vive dentro de un diálogo.
 */
const avisar = (titulo: string, texto: string) => {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') window.alert(`${titulo}\n\n${texto}`);
    return;
  }
  Alert.alert(titulo, texto);
};

/** Sí/No que también funciona en web. Tocar fuera o cancelar cuenta como No. */
const preguntar = (titulo: string, texto: string, textoSi: string, textoNo: string): Promise<boolean> =>
  new Promise((resolver) => {
    if (Platform.OS === 'web') {
      resolver(typeof window !== 'undefined' && window.confirm(`${titulo}\n\n${texto}`));
      return;
    }
    Alert.alert(
      titulo,
      texto,
      [
        { text: textoNo, style: 'cancel', onPress: () => resolver(false) },
        { text: textoSi, onPress: () => resolver(true) },
      ],
      { cancelable: true, onDismiss: () => resolver(false) }
    );
  });

export default function RestaurantRegistrationScreen() {
  const router = useRouter();
  // (el BackHandler de Android se registra más abajo, cuando `step` ya existe)
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [logoUri, setLogoUri] = useState<string | null>(null);
  const [coverUri, setCoverUri] = useState<string | null>(null);
  const [uploadingKind, setUploadingKind] = useState<'logo' | 'cover' | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    cedula: '',
    // ⚠️ VACÍA A PROPÓSITO. Antes venía con «Comida Dominicana» puesta.
    // Dos problemas, y el segundo es el grave:
    //  · Es el nombre del negocio de otro. Un café, una farmacia o un
    //    supermercado quedaban registrados como comida criolla si no tocaban
    //    nada — y con el selector roto (ver `pickCategory`) la mayoría no podía
    //    tocarlo aunque quisiera.
    //  · Decisión del fundador: lo que el usuario ve no lleva marca país.
    // Ahora la elige el dueño, y el paso 2 no deja seguir sin ella.
    category: '',
  });
  // FIX AUDIT — Coordenadas GPS del local. Antes el botón "Fijar GPS" no hacía
  // nada → el restaurante quedaba sin location.lat/lng → foodDispatch dejaba
  // cada pedido "en pool" (nunca se asignaba repartidor).
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [capturingGps, setCapturingGps] = useState(false);
  // VERIFICACIÓN — Foto EN TIEMPO REAL del local (cámara, no galería). Confirma
  // que el negocio existe físicamente en la dirección declarada.
  const [storefrontUri, setStorefrontUri] = useState<string | null>(null);
  const [storefrontUrl, setStorefrontUrl] = useState<string | null>(null);
  const [uploadingStorefront, setUploadingStorefront] = useState(false);

  // Selector de categoría. Reemplaza el dropdown que NO tenía onPress
  // (Google Play lo marcó como UI element unresponsive). Mismas categorías
  // que muestra /food.tsx a los clientes — un dato solo, sin cambios de UX.
  /**
   * ⚠️ ESTO ERA UN `Alert.alert` CON NUEVE BOTONES, Y ANDROID SOLO PINTA TRES.
   *
   * `Alert.alert` de React Native admite un máximo de 3 botones en Android: los
   * demás los descarta EN SILENCIO, sin aviso ni error. Con 8 categorías más
   * «Cancelar», el dueño de un sushi, un café, un supermercado o una farmacia
   * abría el selector y solo veía «Comida Dominicana», «Pizza» y
   * «Hamburguesas». No podía registrarse con lo que de verdad es.
   *
   * Y como «Cancelar» iba el último, también se perdía: el diálogo no se podía
   * cerrar sin elegir una de esas tres.
   *
   * En web es peor todavía: `Alert.alert` de react-native-web no pinta NADA, así
   * que el botón no hacía absolutamente nada.
   *
   * Un modal propio no tiene tope de opciones, se ve igual en Android, iPhone y
   * web, y se puede cerrar. Es además como lo resuelven las apps del ramo.
   */
  const [selectorAbierto, setSelectorAbierto] = useState(false);

  const captureStorefront = async () => {
    if (!userFirebaseUid) {
      avisar('Sesión Requerida', 'Inicia sesión para verificar tu local.');
      return;
    }
    try {
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (!perm.granted) {
        avisar('Permiso de cámara', 'Necesitamos la cámara para la foto en tiempo real de tu local.');
        return;
      }
      // launchCameraAsync = foto EN VIVO (no permite elegir de galería).
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.85,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const uri = result.assets[0].uri;
      setStorefrontUri(uri);
      setUploadingStorefront(true);
      const url = await storageService.uploadRestaurantImage(userFirebaseUid, 'storefront', uri);
      setStorefrontUrl(url);
    } catch (e: any) {
      console.error('storefront upload error', e);
      avisar('Error', mensajeDeError(e, 'No pudimos subir la foto del local. Revisa tu conexión e intenta de nuevo.'));
      setStorefrontUri(null);
    } finally {
      setUploadingStorefront(false);
    }
  };

  const captureGps = async () => {
    if (capturingGps) return;
    setCapturingGps(true);
    try {
      const Location = await import('expo-location');
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        avisar('Permiso requerido', 'Activa el permiso de ubicación para fijar dónde queda tu negocio.');
        return;
      }
      const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High });
      setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    } catch (e: any) {
      avisar('Error', 'No pudimos obtener tu ubicación. Párate en la puerta del local e intenta de nuevo.');
    } finally {
      setCapturingGps(false);
    }
  };

  const pickAndUpload = async (kind: 'logo' | 'cover') => {
    if (!userFirebaseUid) {
      avisar('Sesión Requerida', 'Inicia sesión para subir imágenes a tu negocio.');
      return;
    }
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: kind === 'logo' ? [1, 1] : [16, 9],
        quality: 0.85,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const uri = result.assets[0].uri;
      if (kind === 'logo') setLogoUri(uri);
      else setCoverUri(uri);

      setUploadingKind(kind);
      // storageService.uploadRestaurantImage acepta URI directo y usa putFile
      // nativo en móvil — sin fetch→blob (que se colgaba en algunos Androids).
      const url = await storageService.uploadRestaurantImage(userFirebaseUid, kind, uri);
      if (kind === 'logo') setLogoUrl(url);
      else setCoverUrl(url);
    } catch (e: any) {
      console.error('upload error', e);
      avisar('Error', mensajeDeError(e, 'No pudimos subir la imagen. Revisa tu conexión e intenta de nuevo.'));
      if (kind === 'logo') setLogoUri(null);
      else setCoverUri(null);
    } finally {
      setUploadingKind(null);
    }
  };

  /**
   * ══════════════════════════════════════════════════════════════════════
   * EL BOTÓN FÍSICO DE ATRÁS DE ANDROID TAMBIÉN RETROCEDE DE PASO
   * ══════════════════════════════════════════════════════════════════════
   *
   * Esta pantalla no tenía ningún `BackHandler`, así que el botón de abajo del
   * teléfono no sabía nada de los 3 pasos. Hacía una de estas dos, y ninguna
   * es buena:
   *
   *   · Con historial detrás: se llevaba la pantalla ENTERA desde el paso 2 o
   *     el 3, con todo lo escrito dentro. Rellenar tres pantallas y perderlo
   *     por tocar el botón de siempre.
   *   · Sin historial (que es el caso normal aquí, porque todo el embudo
   *     navega con `replace`): al segundo toque CIERRA LA APP.
   *
   * La flecha del header sí retrocede de paso. Ahora el botón físico hace
   * exactamente lo mismo, que es lo que cualquiera espera.
   *
   * Solo vive mientras `step > 1`: en el paso 1 no hay nada que deshacer y el
   * comportamiento de siempre (salir) es el correcto. Y solo en Android —en
   * iOS no existe ese botón y en web lo gestiona el navegador.
   */
  React.useEffect(() => {
    if (Platform.OS !== 'android' || step <= 1) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      setStep((s) => (s > 1 ? s - 1 : s));
      // `true` = consumido. Si se devolviera `false`, Android seguiría adelante
      // y se llevaría la pantalla igual que antes.
      return true;
    });
    return () => sub.remove();
  }, [step]);

  const handleNext = async () => {
    if (step === 1) {
      // ══════════════════════════════════════════════════════════════════
      // LOS DATOS SE COMPRUEBAN DE VERDAD, NO SOLO «QUE NO ESTÉ VACÍO»
      // ══════════════════════════════════════════════════════════════════
      //
      // Lo que había era esto, entero:
      //     if (!formData.name || !formData.phone || !formData.email)
      //     if (cedulaDigits.length < 10)
      //
      // O sea que pasaban: nombre "a", nombre " " (un espacio es truthy),
      // teléfono "1" o "aaa", correo "x" sin arroba, y cédula "00000000000".
      // El fundador lo probó el 4-oct-2026: «las casillas se pueden llenar
      // con datos a lo loco y sigue avanzando, y el número de teléfono
      // también pasa aunque ese número sea falso».
      //
      // No es cosmético. Estos datos van al catálogo PÚBLICO, el teléfono es
      // por donde el cliente llama al negocio, y el correo es lo que
      // `functions/src/admin.ts` le pasa a Stripe para abrirle su cuenta de
      // cobro — un correo malformado deja al negocio aprobado y sin poder
      // cobrar, en silencio.
      //
      // Cada comprobación vive en `src/utils/` y devuelve el motivo escrito
      // para la persona, no un «formato inválido» que no dice qué corregir.
      const nombre = String(formData.name ?? '').trim();
      if (nombre.length < 3) {
        avisar(
          'Falta el nombre del negocio',
          'Escribe el nombre con el que te conocen tus clientes. Son al menos 3 letras.'
        );
        return;
      }
      if (nombre.length > 120) {
        avisar('Nombre demasiado largo', 'El nombre del negocio no puede pasar de 120 caracteres.');
        return;
      }

      const tel = validarTelefono(formData.phone, 'negocio');
      if (!tel.ok) {
        avisar('Revisa el teléfono', tel.motivo);
        return;
      }

      const correo = validarCorreo(formData.email, 'negocio');
      if (!correo.ok) {
        avisar('Revisa el correo', correo.motivo);
        return;
      }

      const ced = validarCedula(formData.cedula);
      if (!ced.ok) {
        avisar('Revisa la cédula', ced.motivo);
        return;
      }

      // Se guarda lo LIMPIO, no lo que se tecleó: sin espacios al borde y el
      // correo en minúsculas. Si no, dos negocios con el mismo correo escrito
      // distinto parecen dos correos distintos.
      setFormData((d) => ({
        ...d,
        name: nombre,
        description: String(d.description ?? '').trim().slice(0, 500),
        phone: String(d.phone ?? '').trim(),
        email: correo.correo,
        cedula: ced.digitos,
      }));
      setStep(2);
    } else if (step === 2) {
      const direccion = String(formData.address ?? '').trim();
      if (direccion.length < 10) {
        avisar(
          'Falta la dirección',
          'Escribe la dirección completa: calle, número, sector y ciudad. Es la que lee el mensajero ' +
            'cuando va a buscar tu pedido.'
        );
        return;
      }
      if (direccion.length > 300) {
        avisar('Dirección demasiado larga', 'Resúmela: calle, número, sector y ciudad.');
        return;
      }
      if (direccion !== formData.address) {
        setFormData((d) => ({ ...d, address: direccion }));
      }
      // Antes no se validaba NUNCA: se daba por hecho que el valor por defecto
      // siempre estaba. Al quitarlo, sin esto se podría registrar un negocio
      // sin categoría — y un restaurante sin categoría no aparece en ninguna.
      if (!formData.category) {
        avisar(
          'Elige tu categoría',
          'Toca «Categoría Principal» y elige la que mejor describe tu negocio. Es lo que hace que tus clientes te encuentren.'
        );
        return;
      }
      if (!storefrontUrl) {
        avisar('Foto del local requerida', 'Toma la foto en vivo de tu local para verificar el negocio antes de continuar.');
        return;
      }
      if (!gpsCoords) {
        // ⚠️ ESTA PREGUNTA ERA UN CALLEJÓN SIN SALIDA EN WEB.
        //
        // El único camino a `setStep(3)` vivía dentro del `onPress` de un
        // `Alert.alert` de dos botones, y en web ese Alert no pinta nada. O
        // sea que quien no pudiera fijar el GPS en el navegador —permiso
        // denegado, o una página sin candado— NO LLEGABA NUNCA al paso 3, y
        // el botón no le decía por qué. Ahora se pregunta con `preguntar`,
        // que en web usa el diálogo del navegador.
        const sigue = await preguntar(
          'Sin ubicación GPS',
          'Sin fijar el GPS, los mensajeros no podrán ubicar tu negocio automáticamente y tendrán ' +
            'que guiarse solo por la dirección que escribiste. ¿Continuar de todos modos?',
          'Continuar sin GPS',
          'Fijar GPS'
        );
        if (!sigue) return;
        setStep(3);
        return;
      }
      setStep(3);
    } else {
      // Finalizar — persistir en Firestore
      if (!userFirebaseUid) {
        avisar(
          'Sesión Requerida',
          'Inicia sesión para registrar tu negocio. Tu solicitud quedará vinculada a tu cuenta de Go Safely.'
        );
        return;
      }
      if (!logoUrl || !coverUrl) {
        avisar('Faltan imágenes', 'Sube el logo y la foto de portada antes de enviar.');
        return;
      }
      setSubmitting(true);
      try {
        // El doc del restaurante usa el uid del owner como id, igual que en el
        // resto del repo. Las reglas exigen ownerId === request.auth.uid y
        // role==='RESTAURANT_OWNER'.
        // SEGURIDAD (auditoría) — /restaurants es de LECTURA PÚBLICA (catálogo
        // visible sin login), así que la CÉDULA del dueño NO puede ir en el doc
        // principal: quedaba scrapeable con un curl. Va a la subcolección
        // privada /restaurants/{id}/private/verification (dueño + admin).
        const { cedula, ...publicData } = formData;
        await firestoreService.createRestaurant(userFirebaseUid, {
          ...publicData,
          ownerId: userFirebaseUid,
          status: 'PENDING_APPROVAL', // food.tsx filtra y solo muestra APPROVED
          // Coordenadas del local para el auto-dispatch de repartidores.
          location: gpsCoords ? { lat: gpsCoords.lat, lng: gpsCoords.lng } : null,
          storefrontUrl,
          logoUrl,
          coverUrl,
          isPremium: false,
          // NO SE GUARDA `rating`. Antes nacía con 5.0 y cero reseñas, y la
          // ficha del restaurante lo imprimía como "5.0 (0+)". Un documento
          // SIN el campo es "sin dato", y `ratingUtils.ratingDisplay` ya lo
          // traduce a "Nuevo". Las notas reales las agrega
          // functions/src/ratings.ts cuando un cliente califica un pedido.
          reviews: 0,
          deliveryTime: '30-45 min',
          deliveryFee: 100,
          menu: [],
          activePromotions: [],
        });

        // Verificación PRIVADA (cédula del dueño + foto en vivo del local).
        // Solo el dueño y el admin pueden leerla — nunca el catálogo público.
        await firestoreService
          .setRestaurantVerification(userFirebaseUid, {
            cedula: String(cedula).replace(/\D/g, ''),
            storefrontUrl,
            submittedAt: Date.now(),
          })
          .catch((e: any) => console.warn('[restaurant] verificación privada falló:', e?.message));

        // ⚠️ LA SALIDA VA FUERA DEL AVISO, Y ESTE ORDEN ES EL ARREGLO.
        //
        // Antes la ÚNICA navegación de salida de esta pantalla vivía dentro
        // del `onPress` del aviso de éxito:
        //     [{ text: 'Entendido', onPress: () => router.replace('/') }]
        //
        // En web `Alert.alert` no pinta nada, así que ese `onPress` no se
        // disparaba JAMÁS: la solicitud se guardaba bien en Firestore y la
        // persona se quedaba encerrada mirando un formulario ya enviado, sin
        // saber si había funcionado. Es lo que el fundador describió como «el
        // usuario queda atrapado, no lo deja salir».
        //
        // Primero se avisa (que en web es `window.alert` y BLOQUEA hasta que
        // se acepta, así que da tiempo a leerlo) y después se navega, pase lo
        // que pase con el aviso. La navegación ya no depende de que un
        // diálogo exista.
        avisar(
          'Solicitud Enviada',
          'Tu negocio está en revisión. Go Safely validará tus datos en 24 horas y te daremos acceso a tu panel para subir tus productos. ¡Cero Comisiones!'
        );
        router.replace('/');
      } catch (e: any) {
        console.error('Error registrando restaurante:', e);
        avisar(
          'Error',
          mensajeDeError(e, 'No pudimos enviar tu solicitud. Verifica tu conexión e intenta de nuevo.')
        );
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.header}>
          {/* ══ LA SALIDA NO PUEDE PASAR POR LA PUERTA QUE RE-ENRUTA ═══════
              El fundador: «al entrar a ese portal tiene delay, el usuario queda
              atrapado, no lo deja salir aunque use la flecha de salida». La
              flecha funcionaba; lo que estaba roto era el VIAJE DE VUELTA, y es
              un bucle de tres saltos que se cierra sobre sí mismo:

                1. `restaurant-dashboard.tsx` no encuentra el documento del
                   negocio y hace `replace('/restaurant-registration')`. Todo
                   el embudo navega con `replace`, así que la pila queda vacía
                   y `canGoBack()` es false.
                2. La flecha llamaba a `volver(router)`, que sin historial va a
                   su destino de rescate: `'/'`.
                3. Pero `'/'` está en `POST_LOGIN_ENTRY_ROUTES`, así que el
                   guardián de `_layout.tsx` lee el perfil —ESA espera es el
                   «delay» que él describe— y hace
                   `replace(rutaSegunRol('RESTAURANT_OWNER'))`, que es
                   `/restaurant-dashboard`… y vuelta al paso 1.

              Y no se sale ni reiniciando: `setRole` se ejecuta en cuanto se
              toca la tarjeta «Tengo un Negocio» en la portada, antes incluso de
              mirar si hay sesión. O sea que basta con ASOMARSE para quedar
              marcado como dueño y caer en el bucle en cada arranque.

              El arreglo es el destino: Perfil no es ruta de entrada, así que el
              guardián no corre, y desde ahí la tarjeta «Cambiar a Pasajero»
              —que existe para todos los roles— le devuelve su app. No se toca
              `rutaSegunRol` ni el guardián: eso enruta también al pasajero y al
              chofer, y el fundador pidió expresamente no tocar los viajes. */}
          <TouchableOpacity onPress={() => step > 1 ? setStep(step - 1) : volver(router, '/(tabs)/profile')} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={Colors.white} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Suma tu Negocio</Text>
          <View style={{ width: 24 }} />
        </View>

        {/* PROGRESS BAR */}
        <View style={styles.progressContainer}>
          <View style={[styles.progressBar, { width: `${(step / 3) * 100}%` }]} />
        </View>

        <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 50 }}>
          {step === 1 && (
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Datos del Negocio</Text>
              <Text style={styles.stepSubtitle}>Ingresa la información pública de tu negocio.</Text>

              {/* ══ AQUÍ SE REGISTRA CUALQUIER NEGOCIO, Y NO HAY OTRO SITIO ══
                  Petición literal del fundador (4-oct-2026): «en ese apartado
                  debe decir que en ninguna otra parte están supermercado,
                  farmacia entre otros negocios a registrar».

                  Hace falta porque TODO lo que rodea a esta pantalla sigue
                  llamándose «restaurante» —la ruta, la colección, el panel— y
                  el dueño de una farmacia que llega hasta aquí tiene motivos
                  para dudar de si se equivocó de puerta. Esos nombres internos
                  NO se tocan (renombrarlos rompería los documentos ya
                  guardados y los enlaces), así que lo que se arregla es lo
                  único que la persona ve: el texto. */}
              <View style={styles.avisoDeNegocios}>
                <Ionicons name="storefront-outline" size={20} color={Colors.primary} />
                <Text style={styles.avisoDeNegociosTexto}>
                  <Text style={{ fontWeight: '700' }}>Este es el único lugar para registrar tu negocio</Text>
                  , sea el que sea: restaurante, supermercado, farmacia, minimarket, ferretería,
                  repostería, licorería o cualquier otro. No hay otra pantalla ni otro formulario —
                  si no ves tu tipo de negocio en la lista de categorías, elige{' '}
                  <Text style={{ fontWeight: '700' }}>«Otro negocio»</Text> y entras igual.
                </Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Nombre del Negocio *</Text>
                <TextInput style={styles.input} placeholder="Ej. Farmacia La Salud, Supermercado El Ahorro…" placeholderTextColor="#888" value={formData.name} onChangeText={(t) => setFormData({...formData, name: t})} />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Descripción Breve</Text>
                <TextInput style={[styles.input, { height: 80, textAlignVertical: 'top' }]} placeholder="Ej. Lo que vendes y qué te hace distinto…" placeholderTextColor="#888" multiline value={formData.description} onChangeText={(t) => setFormData({...formData, description: t})} />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Teléfono Local *</Text>
                <TextInput style={styles.input} placeholder="809-XXX-XXXX" keyboardType="phone-pad" placeholderTextColor="#888" value={formData.phone} onChangeText={(t) => setFormData({...formData, phone: t})} />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Correo Electrónico *</Text>
                <TextInput style={styles.input} placeholder="contacto@tunegocio.com" keyboardType="email-address" placeholderTextColor="#888" value={formData.email} onChangeText={(t) => setFormData({...formData, email: t})} />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Cédula del Dueño *</Text>
                <TextInput style={styles.input} placeholder="001-1234567-3" keyboardType="number-pad" placeholderTextColor="#888" value={formData.cedula} onChangeText={(t) => setFormData({...formData, cedula: t})} />
                <Text style={styles.helperNote}>La usamos para verificar la titularidad del negocio.</Text>
              </View>
            </View>
          )}

          {step === 2 && (
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Ubicación y Logística</Text>
              <Text style={styles.stepSubtitle}>¿Dónde irá el mensajero a recoger los pedidos de tu negocio?</Text>
              
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Dirección Completa *</Text>
                <TextInput style={[styles.input, { height: 80, textAlignVertical: 'top' }]} placeholder="Calle, Número, Sector, Ciudad" placeholderTextColor="#888" multiline value={formData.address} onChangeText={(t) => setFormData({...formData, address: t})} />
              </View>

              <TouchableOpacity
                style={[styles.gpsButton, gpsCoords ? { backgroundColor: '#00A86B' } : null]}
                onPress={captureGps}
                disabled={capturingGps}
              >
                {capturingGps ? (
                  <ActivityIndicator color="#FFF" style={{ marginRight: 8 }} />
                ) : (
                  <Ionicons name={gpsCoords ? 'checkmark-circle' : 'location'} size={20} color="#FFF" style={{ marginRight: 8 }} />
                )}
                <Text style={styles.gpsButtonText}>
                  {capturingGps ? 'Obteniendo ubicación…' : gpsCoords ? 'Ubicación fijada ✓' : 'Fijar GPS (párate en el local)'}
                </Text>
              </TouchableOpacity>

              {/* FOTO EN TIEMPO REAL DEL LOCAL (verificación) — cámara, no galería */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Foto del Local (en vivo) *</Text>
                <Text style={styles.helperNote}>Toma una foto AHORA de tu local (casa o establecimiento). Verifica que el negocio existe en la dirección que pusiste.</Text>
                <TouchableOpacity
                  style={[styles.gpsButton, { marginTop: 8 }, storefrontUrl ? { backgroundColor: '#00A86B' } : { backgroundColor: Colors.primary }]}
                  onPress={captureStorefront}
                  disabled={uploadingStorefront}
                >
                  {uploadingStorefront ? (
                    <ActivityIndicator color="#FFF" style={{ marginRight: 8 }} />
                  ) : (
                    <Ionicons name={storefrontUrl ? 'checkmark-circle' : 'camera'} size={20} color="#FFF" style={{ marginRight: 8 }} />
                  )}
                  <Text style={styles.gpsButtonText}>
                    {uploadingStorefront ? 'Subiendo…' : storefrontUrl ? 'Foto del local lista ✓' : 'Tomar foto del local'}
                  </Text>
                </TouchableOpacity>
                {storefrontUri ? (
                  <Image source={{ uri: storefrontUri }} style={{ width: '100%', height: 160, borderRadius: 12, marginTop: 10 }} resizeMode="cover" />
                ) : null}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Categoría Principal</Text>
                <TouchableOpacity
                  style={styles.dropdownInput}
                  onPress={() => setSelectorAbierto(true)}
                  activeOpacity={0.8}
                  accessibilityRole="button"
                  accessibilityLabel="Elegir la categoría principal del negocio"
                >
                  {/* Se guarda el id y se enseña el nombre: son dos cosas
                      distintas a propósito. Ver constants/categoriasDeNegocio. */}
                  <Text style={{ color: formData.category ? Colors.text : Colors.textSecondary }}>
                    {nombreDeCategoria(formData.category) ?? 'Elige una categoría'}
                  </Text>
                  <Ionicons name="chevron-down" size={20} color={Colors.textSecondary} />
                </TouchableOpacity>
              </View>
            </View>
          )}

          {step === 3 && (
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Identidad Visual</Text>
              <Text style={styles.stepSubtitle}>Sube las fotos que verán los clientes en la app.</Text>
              
              <View style={styles.photoContainer}>
                <TouchableOpacity
                  style={[styles.photoUploadBox, logoUri && { borderStyle: 'solid', borderColor: Colors.primary }]}
                  onPress={() => pickAndUpload('logo')}
                  disabled={uploadingKind === 'logo'}
                >
                  {uploadingKind === 'logo' ? (
                    <ActivityIndicator color={Colors.primary} />
                  ) : logoUri ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 8 }}>
                      <Image source={{ uri: logoUri }} style={{ width: 60, height: 60, borderRadius: 8 }} />
                      <View>
                        <Text style={{ fontSize: 14, fontWeight: 'bold', color: Colors.text }}>
                          {logoUrl ? '✓ Logo subido' : 'Subiendo…'}
                        </Text>
                        <Text style={{ fontSize: 11, color: Colors.textSecondary }}>Toca para cambiar</Text>
                      </View>
                    </View>
                  ) : (
                    <>
                      <Ionicons name="camera" size={32} color={Colors.textSecondary} />
                      <Text style={styles.photoUploadText}>Subir Logo del Negocio</Text>
                    </>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.photoUploadBox,
                    { height: 150, marginTop: 20 },
                    coverUri && { borderStyle: 'solid', borderColor: Colors.primary, padding: 0, overflow: 'hidden' },
                  ]}
                  onPress={() => pickAndUpload('cover')}
                  disabled={uploadingKind === 'cover'}
                >
                  {uploadingKind === 'cover' ? (
                    <ActivityIndicator color={Colors.primary} />
                  ) : coverUri ? (
                    <View style={{ width: '100%', height: '100%', position: 'relative' }}>
                      <Image source={{ uri: coverUri }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                      <View style={{ position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.6)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 }}>
                        <Text style={{ color: '#FFF', fontSize: 11, fontWeight: 'bold' }}>
                          {coverUrl ? '✓ Subida' : 'Subiendo…'}
                        </Text>
                      </View>
                    </View>
                  ) : (
                    <>
                      <Ionicons name="image" size={32} color={Colors.textSecondary} />
                      <Text style={styles.photoUploadText}>Subir Foto de Portada (Banner)</Text>
                      <Text style={{ fontSize: 10, color: '#888', marginTop: 5 }}>Resolución recomendada: 800x400</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>

              <View style={styles.infoAlert}>
                <Ionicons name="information-circle" size={24} color={Colors.primary} />
                <Text style={styles.infoAlertText}>
                  Go Safely no cobra comisiones por venta ni cuota mensual al negocio. Solo pagas si decides contratar publicidad, que es opcional. El envío lo acuerdas tú directamente con el mensajero que lleva tus pedidos.
                </Text>
              </View>
            </View>
          )}

          <Button
            title={step === 3 ? (submitting ? 'Enviando…' : 'Enviar Solicitud') : 'Continuar'}
            onPress={handleNext}
            loading={submitting}
            style={{ marginTop: 30 }}
          />
        </ScrollView>
      </SafeAreaView>

      {/* ── EL SELECTOR DE CATEGORÍA ──────────────────────────────────────
          Lista completa, con la elegida marcada, y se puede cerrar tocando
          fuera o el botón. Ver la nota de `selectorAbierto`. */}
      <Modal
        visible={selectorAbierto}
        transparent
        animationType="fade"
        onRequestClose={() => setSelectorAbierto(false)}
      >
        <Pressable style={estiloSelector.fondo} onPress={() => setSelectorAbierto(false)}>
          {/* El toque de dentro NO cierra: sin esto, elegir una categoría
              cerraría el modal por el fondo antes de registrar el toque. */}
          <Pressable style={estiloSelector.caja} onPress={(e) => e.stopPropagation()}>
            <Text style={estiloSelector.titulo}>Categoría principal</Text>
            <Text style={estiloSelector.ayuda}>
              Elige la que mejor describe tu negocio. Es la que usan tus clientes para encontrarte.
            </Text>

            <ScrollView style={{ maxHeight: 360 }} showsVerticalScrollIndicator>
              {CATEGORIAS_DE_NEGOCIO.map((cat) => {
                const elegida = formData.category === cat.id;
                return (
                  <TouchableOpacity
                    key={cat.id}
                    style={[estiloSelector.fila, elegida && estiloSelector.filaElegida]}
                    onPress={() => {
                      setFormData((prev) => ({ ...prev, category: cat.id }));
                      setSelectorAbierto(false);
                    }}
                    accessibilityRole="button"
                    accessibilityState={{ selected: elegida }}
                  >
                    <Text style={estiloSelector.icono}>{cat.icono}</Text>
                    <Text style={[estiloSelector.nombre, elegida && { fontWeight: '800' }]}>
                      {cat.nombre}
                    </Text>
                    {elegida ? (
                      <Ionicons name="checkmark-circle" size={21} color={Colors.primary} />
                    ) : null}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>

            <TouchableOpacity
              style={estiloSelector.cerrar}
              onPress={() => setSelectorAbierto(false)}
              accessibilityRole="button"
            >
              <Text style={estiloSelector.cerrarTexto}>Cancelar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: 'rgba(10, 46, 115, 0.9)' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: SIZES.padding },
  backButton: { padding: 5 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: Colors.white },
  
  progressContainer: { height: 4, backgroundColor: 'rgba(255,255,255,0.2)' },
  progressBar: { height: '100%', backgroundColor: Colors.accent },

  container: { flex: 1, padding: SIZES.padding },
  stepContent: { backgroundColor: '#FFF', borderRadius: SIZES.radius, padding: 20, marginTop: 10 },
  stepTitle: { fontSize: 22, fontWeight: 'bold', color: Colors.text, marginBottom: 5 },
  stepSubtitle: { fontSize: 14, color: Colors.textSecondary, marginBottom: 25 },

  inputGroup: { marginBottom: 20 },
  label: { fontSize: 14, fontWeight: '600', color: Colors.text, marginBottom: 8 },
  helperNote: { fontSize: 12, color: Colors.textSecondary, marginTop: 6, lineHeight: 16 },
  input: { backgroundColor: '#F5F5F5', borderRadius: 8, padding: 15, fontSize: 15, color: Colors.text, borderWidth: 1, borderColor: '#E0E0E0' },
  
  gpsButton: { backgroundColor: Colors.primary, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 15, borderRadius: 8, marginBottom: 20 },
  gpsButtonText: { color: '#FFF', fontWeight: 'bold', fontSize: 15 },

  dropdownInput: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F5F5F5', borderRadius: 8, padding: 15, borderWidth: 1, borderColor: '#E0E0E0' },

  photoContainer: { marginBottom: 20 },
  photoUploadBox: { height: 100, backgroundColor: '#F9F9F9', borderWidth: 2, borderColor: '#E0E0E0', borderStyle: 'dashed', borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  photoUploadText: { fontSize: 14, color: Colors.textSecondary, marginTop: 10, fontWeight: '500' },

  infoAlert: { flexDirection: 'row', backgroundColor: '#E3F2FD', padding: 15, borderRadius: 8, marginTop: 10 },
  // El aviso de «aquí se registra cualquier negocio». Mismo patrón que
  // `infoAlert` pero en el verde de la marca, para que no se lea como una
  // advertencia sino como una aclaración.
  avisoDeNegocios: { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#E8F5E9', padding: 14, borderRadius: 8, marginTop: 4, marginBottom: 18 },
  avisoDeNegociosTexto: { flex: 1, fontSize: 12.5, color: '#1B5E20', marginLeft: 10, lineHeight: 18 },
  infoAlertText: { flex: 1, fontSize: 12, color: '#0277BD', marginLeft: 10, lineHeight: 18 }
});

// Aparte del StyleSheet de la pantalla, para que se vea de un vistazo qué se
// añadió al sustituir el selector roto.
const estiloSelector = StyleSheet.create({
  fondo: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  caja: {
    backgroundColor: '#FFF',
    borderRadius: 18,
    paddingTop: 20,
    paddingBottom: 10,
    paddingHorizontal: 18,
  },
  titulo: { fontSize: 18, fontWeight: '800', color: Colors.text },
  ayuda: { fontSize: 13, lineHeight: 18, color: Colors.textSecondary, marginTop: 4, marginBottom: 12 },
  fila: {
    flexDirection: 'row',
    alignItems: 'center',
    // 52 px: por debajo de eso se falla con el pulgar.
    height: 52,
    paddingHorizontal: 12,
    borderRadius: 12,
    marginBottom: 4,
  },
  filaElegida: { backgroundColor: 'rgba(32,138,239,0.10)' },
  icono: { fontSize: 20, marginRight: 12 },
  nombre: { flex: 1, fontSize: 15.5, fontWeight: '600', color: Colors.text },
  cerrar: { height: 48, alignItems: 'center', justifyContent: 'center', marginTop: 6 },
  cerrarTexto: { fontSize: 15, fontWeight: '700', color: Colors.textSecondary },
});
